import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { format, addMonths, subMonths, addDays, subDays, startOfMonth, getDay, isSameDay } from 'date-fns';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent } from '../components/ui/dialog';
import DateDetailModal from '../components/DateDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CalendarDay({ dayData, isSelected, isToday, onClick, language, isClickable = true, activationDayOn = false }) {
    const isWeekend = dayData.weekday === 5 || dayData.weekday === 6;
    const isGood = dayData.day_officer.auspicious;
    const isPersonalClash = dayData.is_personal_clash;
    const isDateBreaker = dayData.is_date_breaker;
    const isActivationDay = activationDayOn && isPersonalClash;
    
    let bgClass = 'bg-card border-border';
    if (isActivationDay) bgClass = 'bg-orange-100 dark:bg-orange-900/50 border-orange-400 dark:border-orange-500';
    else if (isDateBreaker) bgClass = 'bg-rose-200 dark:bg-rose-900/50 border-rose-400 dark:border-rose-600';
    else if (isPersonalClash) bgClass = 'bg-purple-100 dark:bg-purple-900/40 border-purple-300';
    else if (dayData.day_type === 'excellent') bgClass = 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300';
    else if (dayData.day_type === 'good') bgClass = 'bg-green-50 dark:bg-green-900/30 border-green-200';
    else if (dayData.day_type === 'poor') bgClass = 'bg-amber-50 dark:bg-amber-900/30 border-amber-200';
    else if (dayData.day_type === 'avoid') bgClass = 'bg-red-100 dark:bg-red-900/40 border-red-300';
    
    let ringClass = '';
    if (isSelected) ringClass = 'ring-2 ring-primary ring-offset-1';
    else if (isToday) ringClass = 'ring-2 ring-yellow-500 ring-offset-1';
    
    // Non-clickable styling for guests
    const clickableClass = isClickable 
        ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]' 
        : 'cursor-default opacity-90';
    
    return (
        <div
            onClick={isClickable ? onClick : undefined}
            className={`relative p-1 sm:p-2 rounded-lg border transition-all duration-200 text-left aspect-square sm:aspect-auto sm:min-h-24 ${bgClass} ${ringClass} ${clickableClass}`}
            data-testid={`calendar-day-${dayData.day}`}
        >
            <div className={`text-sm sm:text-base font-bold ${isWeekend ? 'text-red-500' : ''}`}>
                {dayData.day}
            </div>
            <div className="hidden sm:block text-xs text-muted-foreground truncate">
                {language === 'zh' ? dayData.pillars.day_branch : dayData.pillars.day_animal}
            </div>
            <div className={`text-xs sm:text-sm font-medium mt-0.5 ${isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                <span className="font-chinese">{dayData.day_officer.chinese}</span>
                <span className="hidden sm:inline ml-1 text-muted-foreground text-xs">{dayData.day_officer.english}</span>
            </div>
            <div className="hidden sm:block text-xs text-muted-foreground mt-1">
                {language === 'zh' ? `冲${dayData.clash.animal_chinese}` : `vs ${dayData.clash.animal_english}`}
            </div>
            <div className="absolute top-1 right-1 flex gap-0.5">
                {dayData.path.is_yellow_path && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-yellow-400" title="Yellow Path" />}
                {isActivationDay && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-orange-500" title="Activation Day" />}
                {!isActivationDay && isPersonalClash && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-purple-500" title="Personal Clash" />}
                {isDateBreaker && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-rose-600" title="Date Breaker 日破" />}
            </div>
            {isActivationDay && (
                <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                    <span className="text-[8px] sm:text-[10px] font-bold text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-800/50 px-1 rounded">
                        冲
                    </span>
                </div>
            )}
            {isDateBreaker && (
                <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                    <span className="text-[8px] sm:text-[10px] font-bold text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-800/50 px-1 rounded">
                        日破
                    </span>
                </div>
            )}
        </div>
    );
}

function Dashboard() {
    const { language } = useLanguage();
    const { user, token } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [tongShuData, setTongShuData] = useState(null);
    const [monthlyData, setMonthlyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [monthLoading, setMonthLoading] = useState(false);
    const [mode, setMode] = useState('beginner');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [userProfile, setUserProfile] = useState(user);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [guestName, setGuestName] = useState(null);
    
    // Check for guest animal from URL query params (admin viewing guest profile)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gAnimal = params.get('guest_animal');
        const gIndex = params.get('guest_animal_index');
        const gChinese = params.get('guest_animal_chinese');
        const gName = params.get('guest_name');
        if (gAnimal && gIndex != null) {
            setUserProfile(prev => ({
                ...(prev || {}),
                birth_animal: gAnimal,
                birth_animal_index: parseInt(gIndex),
                birth_animal_chinese: gChinese || '',
            }));
            if (gName) setGuestName(decodeURIComponent(gName));
        }
    }, []);
    
    // Sync userProfile when user loads from auth context (only if not guest mode)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (user && !params.get('guest_animal')) setUserProfile(user);
    }, [user]);
    
    // Legend filter state
    const [filters, setFilters] = useState({
        excellent: true,
        good: true,
        neutral: true,
        poor: true,
        avoid: true,
        dateBreaker: true,
        clash: true,
        yellowPath: true,
        hairCutting: false,
        activationDay: false
    });
    
    // Clash pairs mapping
    const CLASH_PAIRS = {
        rat: 'Horse 午', ox: 'Goat 未', tiger: 'Monkey 申',
        rabbit: 'Rooster 酉', dragon: 'Dog 戌', snake: 'Pig 亥',
        horse: 'Rat 子', goat: 'Ox 丑', monkey: 'Tiger 寅',
        rooster: 'Rabbit 卯', dog: 'Dragon 辰', pig: 'Snake 巳'
    };
    
    const toggleFilter = (filterName) => {
        setFilters(prev => ({ ...prev, [filterName]: !prev[filterName] }));
    };
    
    // Check if a day should be visible based on filters
    const isDayVisible = (dayData) => {
        // Activation Day filter - when ON, only highlight clash days
        if (filters.activationDay && !dayData.is_personal_clash) return false;
        
        // Hair Cutting filter - when ON, only show good hair cutting days
        if (filters.hairCutting && !dayData.is_hair_cutting_good) return false;
        
        if (dayData.is_date_breaker && !filters.dateBreaker) return false;
        if (dayData.is_personal_clash && !filters.clash) return false;
        if (dayData.path?.is_yellow_path && !filters.yellowPath) return false;
        
        if (dayData.day_type === 'excellent' && !filters.excellent) return false;
        if (dayData.day_type === 'good' && !filters.good) return false;
        if (dayData.day_type === 'neutral' && !filters.neutral) return false;
        if (dayData.day_type === 'poor' && !filters.poor) return false;
        if (dayData.day_type === 'avoid' && !filters.avoid) return false;
        
        return true;
    };
    
    // Check if user is admin and has PRO access
    useEffect(() => {
        const checkAdmin = async () => {
            if (!token) return;
            try {
                const response = await fetch(`${API_URL}/api/auth/admin/check`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setIsAdmin(data.is_admin);
                    setHasProAccess(data.has_pro_access);
                    // If user has PRO access, set to practitioner mode
                    if (data.has_pro_access) {
                        setMode('practitioner');
                    } else {
                        setMode('beginner');
                    }
                }
            } catch (error) {
                console.error('Admin check error:', error);
            }
        };
        checkAdmin();
    }, [token]);
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                let url = `${API_URL}/api/tong-shu/daily?date=${dateStr}`;
                if (userProfile?.birth_animal_index != null) {
                    url += `&user_animal_index=${userProfile.birth_animal_index}`;
                }
                const response = await axios.get(url);
                setTongShuData(response.data);
            } catch (error) {
                console.error('Error:', error);
            }
            setLoading(false);
        };
        fetchData();
    }, [selectedDate, userProfile]);
    
    useEffect(() => {
        const fetchMonthData = async () => {
            setMonthLoading(true);
            try {
                const year = selectedDate.getFullYear();
                const month = selectedDate.getMonth() + 1;
                let url = `${API_URL}/api/tong-shu/monthly?year=${year}&month=${month}`;
                if (userProfile?.birth_animal_index != null) {
                    url += `&user_animal_index=${userProfile.birth_animal_index}`;
                }
                const response = await axios.get(url);
                setMonthlyData(response.data);
            } catch (error) {
                console.error('Error:', error);
            }
            setMonthLoading(false);
        };
        fetchMonthData();
    }, [selectedDate, userProfile]);
    
    const handlePrevMonth = () => setSelectedDate(prev => subMonths(prev, 1));
    const handleNextMonth = () => setSelectedDate(prev => addMonths(prev, 1));
    const handleToday = () => setSelectedDate(new Date());
    const handleDayClick = (date) => { 
        if (!token) return; // Prevent click for non-authenticated users
        setSelectedDate(date); 
        setShowDetailModal(true); 
    };
    const handlePrevDay = () => setSelectedDate(prev => subDays(prev, 1));
    const handleNextDay = () => setSelectedDate(prev => addDays(prev, 1));
    
    // Check if user is authenticated
    const isAuthenticated = !!token;
    
    const monthDays = monthlyData?.days || [];
    const monthStart = startOfMonth(selectedDate);
    const startDayOfWeek = getDay(monthStart);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weekDaysZh = ['日', '一', '二', '三', '四', '五', '六'];
    
    const emptyDays = [];
    for (let i = 0; i < startDayOfWeek; i++) {
        emptyDays.push(i);
    }
    
    if (loading && !tongShuData) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background" data-testid="dashboard">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Guest Banner */}
                {guestName && (
                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center justify-between" data-testid="guest-banner">
                        <div className="flex items-center gap-3">
                            <span className="font-chinese text-xl">{userProfile?.birth_animal_chinese}</span>
                            <div>
                                <span className="font-bold text-indigo-800">{guestName}</span>
                                <span className="text-sm text-indigo-600 ml-2">Guest · {userProfile?.birth_animal}</span>
                            </div>
                        </div>
                        <button onClick={() => window.close()} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium px-3 py-1 hover:bg-indigo-100 rounded-lg transition-colors">Close Tab</button>
                    </div>
                )}
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-serif text-2xl sm:text-3xl font-bold">
                            {language === 'zh' ? '通书日历' : 'Tong Shu Calendar'}
                        </h1>
                        <p className="text-muted-foreground mt-1">{format(selectedDate, 'MMMM yyyy')}</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {userProfile?.birth_animal && (
                            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-full border">
                                <span className="font-chinese">{userProfile.birth_animal_chinese}</span>
                                <span className="text-sm text-muted-foreground capitalize">{userProfile.birth_animal}</span>
                            </div>
                        )}
                        
                        <div className="flex items-center gap-2 relative group">
                            <Label htmlFor="mode-toggle" className="text-sm hidden sm:inline">
                                {mode === 'beginner' ? 'Beginner' : 'Pro'}
                            </Label>
                            <Switch 
                                id="mode-toggle" 
                                checked={mode === 'practitioner'} 
                                onCheckedChange={(c) => hasProAccess && setMode(c ? 'practitioner' : 'beginner')} 
                                disabled={!hasProAccess}
                                className={!hasProAccess ? 'opacity-50 cursor-not-allowed' : ''}
                                data-testid="pro-mode-toggle"
                            />
                            {!hasProAccess && (
                                <a href="/subscription" className="absolute -bottom-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-amber-600 text-white text-xs px-3 py-1 rounded whitespace-nowrap z-50 hover:bg-amber-700">
                                    Upgrade to PRO →
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Calendar */}
                <Card data-testid="month-calendar-card">
                    <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                            {isAdmin ? (
                                <Button 
                                    variant="outline" 
                                    size="icon" 
                                    onClick={handlePrevMonth} 
                                    title="Previous month"
                                    data-testid="prev-month-btn"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                            ) : (
                                <div className="w-10"></div>
                            )}
                            <div className="text-center">
                                <CardTitle className="font-serif text-xl">{format(selectedDate, 'MMMM yyyy')}</CardTitle>
                                {/* Solar Month Indicator - Show only the primary solar month */}
                                {monthlyData?.solar_months_in_view && Object.keys(monthlyData.solar_months_in_view).length > 0 && (
                                    <div className="flex items-center justify-center gap-2 mt-1">
                                        {(() => {
                                            // Get the solar month that covers most of this Gregorian month
                                            const solarMonths = Object.values(monthlyData.solar_months_in_view);
                                            // Show the month that starts earliest (primary month for this view)
                                            const primaryMonth = solarMonths.reduce((prev, curr) => 
                                                (prev.start_day || 1) < (curr.start_day || 1) ? prev : curr
                                            );
                                            return (
                                                <div className="flex items-center gap-1 px-3 py-1 bg-primary/10 rounded-full text-sm">
                                                    <span className="font-chinese font-bold text-primary">{primaryMonth.chinese_name}</span>
                                                    <span className="text-muted-foreground">{primaryMonth.english_name}</span>
                                                    {primaryMonth.branch && (
                                                        <span className="text-xs text-muted-foreground">({primaryMonth.branch.chinese})</span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                                <Button variant="link" size="sm" onClick={handleToday} className="text-muted-foreground">
                                    {language === 'zh' ? '回到今天' : 'Go to Today'}
                                </Button>
                            </div>
                            {isAdmin ? (
                                <Button 
                                    variant="outline" 
                                    size="icon" 
                                    onClick={handleNextMonth} 
                                    title="Next month"
                                    data-testid="next-month-btn"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            ) : (
                                <div className="w-10"></div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Legend - Clickable Filters */}
                        <div className="flex flex-wrap gap-2 mb-4 text-xs">
                            <button 
                                onClick={() => toggleFilter('excellent')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.excellent ? 'bg-emerald-100 border-emerald-300' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
                                <span>Excellent</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('good')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.good ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-green-50 border border-green-200" />
                                <span>Good</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('neutral')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.neutral ? 'bg-card border-border' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-card border" />
                                <span>Neutral</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('poor')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.poor ? 'bg-amber-50 border-amber-200' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-amber-50 border border-amber-200" />
                                <span>Poor</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('avoid')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.avoid ? 'bg-red-100 border-red-300' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-red-100 border border-red-300" />
                                <span>Avoid</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('dateBreaker')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.dateBreaker ? 'bg-rose-200 border-rose-400' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-3 h-3 rounded bg-rose-200 border border-rose-400" />
                                <span>日破 Date Breaker</span>
                            </button>
                            {userProfile?.birth_animal && (
                                <button 
                                    onClick={() => toggleFilter('clash')}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.clash ? 'bg-purple-100 border-purple-300' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                                >
                                    <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300" />
                                    <span>Clash</span>
                                </button>
                            )}
                            <button 
                                onClick={() => toggleFilter('yellowPath')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.yellowPath ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                                <span>Yellow Path</span>
                            </button>
                            <button 
                                onClick={() => toggleFilter('hairCutting')}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.hairCutting ? 'bg-teal-100 border-teal-400' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                            >
                                <span>✂️</span>
                                <span>Hair Cutting</span>
                            </button>
                            {userProfile?.birth_animal && (
                                <button 
                                    onClick={() => toggleFilter('activationDay')}
                                    data-testid="activation-day-toggle"
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${filters.activationDay ? 'bg-orange-100 border-orange-400' : 'bg-gray-100 border-gray-300 opacity-50'}`}
                                >
                                    <div className={`w-2 h-2 rounded-full ${filters.activationDay ? 'bg-orange-500' : 'bg-gray-400'}`} />
                                    <span>Activation Day</span>
                                </button>
                            )}
                        </div>
                        
                        {monthLoading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-7 gap-1 sm:gap-2">
                                {/* Weekday headers */}
                                {weekDays.map((day, i) => (
                                    <div key={day} className={`text-center text-xs font-medium py-2 ${i === 0 || i === 6 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                        {language === 'zh' ? weekDaysZh[i] : day}
                                    </div>
                                ))}
                                
                                {/* Empty cells */}
                                {emptyDays.map((i) => (
                                    <div key={`empty-${i}`} className="aspect-square sm:aspect-auto sm:min-h-24" />
                                ))}
                                
                                {/* Days */}
                                {monthDays.map((dayData) => {
                                    const dateObj = new Date(dayData.date);
                                    const visible = isDayVisible(dayData);
                                    return (
                                        <div 
                                            key={dayData.day}
                                            className={`transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-20'}`}
                                        >
                                            <CalendarDay
                                                dayData={dayData}
                                                isSelected={isSameDay(dateObj, selectedDate)}
                                                isToday={isSameDay(dateObj, new Date())}
                                                onClick={() => visible && handleDayClick(dateObj)}
                                                language={language}
                                                isClickable={isAuthenticated && visible}
                                                activationDayOn={filters.activationDay}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
                
                {/* Login Prompt for Guests */}
                {!isAuthenticated && (
                    <Card className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
                        <CardContent className="pt-6 text-center">
                            <Lock className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                                {language === 'zh' ? '登入查看詳情' : 'Login to View Details'}
                            </h3>
                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 mb-4">
                                {language === 'zh' ? '註冊或登入以查看每日詳細分析' : 'Register or login to view detailed daily analysis'}
                            </p>
                            <div className="flex gap-3 justify-center">
                                <Button variant="outline" onClick={() => window.location.href = '/login'}>
                                    {language === 'zh' ? '登入' : 'Login'}
                                </Button>
                                <Button onClick={() => window.location.href = '/register'}>
                                    {language === 'zh' ? '註冊' : 'Register'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* Quick Info - Only for authenticated users */}
                {isAuthenticated && tongShuData && (
                    <Card className="mt-6" data-testid="selected-day-info">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-serif text-lg">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                    <div className="font-chinese text-2xl font-bold">{tongShuData.dong_gong?.chinese}</div>
                                    <div className="text-xs text-muted-foreground">{tongShuData.dong_gong?.english}</div>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                    <div className={`font-chinese text-2xl font-bold ${tongShuData.day_officer?.auspicious ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {tongShuData.day_officer?.chinese}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{tongShuData.day_officer?.english}</div>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                    <div className="font-chinese text-2xl font-bold">{tongShuData.pillars?.day?.combined_chinese}</div>
                                    <div className="text-xs text-muted-foreground">Day Pillar</div>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                    <div className="font-chinese text-2xl font-bold">{tongShuData.lunar_mansion?.chinese}</div>
                                    <div className="text-xs text-muted-foreground">{tongShuData.lunar_mansion?.english}</div>
                                </div>
                            </div>
                            <div className="mt-4">
                                <Button variant="outline" onClick={() => setShowDetailModal(true)} className="w-full" data-testid="view-details-btn">
                                    {language === 'zh' ? '查看详情' : 'View Full Details'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
            
            {/* Modal - Only for authenticated users */}
            {isAuthenticated && showDetailModal && tongShuData && (
                <DateDetailModal
                    data={tongShuData}
                    date={selectedDate}
                    onClose={() => setShowDetailModal(false)}
                    onPrevDay={handlePrevDay}
                    onNextDay={handleNextDay}
                    userAnimal={userProfile?.birth_animal}
                    mode={mode}
                    hasProAccess={hasProAccess}
                />
            )}
        </div>
    );
}

export default Dashboard;
